import { useState, useEffect, useMemo } from 'react'
import { VendorProduct, Vendor } from '../types'
import { mockVendors } from '../data/mockVendors'

interface SearchFilters {
  query: string
  productType: 'all' | '7oh' | 'mit' | 'plain-leaf'
  sourceType: 'all' | 'thai' | 'domestic'
  chemicalForm: 'all' | 'salt' | 'freebase'
  format: 'all' | 'powder' | 'tablet' | 'capsule'
  effects: string[]
  priceRange: [number, number]
  minRating: number
  sortBy: 'relevance' | 'price-low' | 'price-high' | 'rating' | 'newest' | 'popular'
  showFeaturedFirst: boolean
}

interface SearchResult {
  product: VendorProduct
  vendor: Vendor
  score: number
  isFeatured: boolean
  isPremiumVendor: boolean
}

const effectOptions = [
  'Sedating',
  'Energetic', 
  'Euphoric',
  'Pain Relieving'
]

const productTypeLabels = {
  'all': 'All Products',
  '7oh': '7OH Products',
  'mit': 'MIT Products',
  'plain-leaf': 'Plain Leaf'
}

interface EnhancedSearchProps {
  onResults?: (results: SearchResult[]) => void
  showFilters?: boolean
  compactMode?: boolean
  initialQuery?: string
}

export default function EnhancedSearch({ onResults, showFilters = true, compactMode = false, initialQuery = '' }: EnhancedSearchProps) {
  const [filters, setFilters] = useState<SearchFilters>({
    query: '',
    productType: 'all',
    sourceType: 'all',
    chemicalForm: 'all',
    format: 'all',
    effects: [],
    priceRange: [0, 500],
    minRating: 0,
    sortBy: 'relevance',
    showFeaturedFirst: true
  })
  
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [isSearching, setIsSearching] = useState(false)

  // Set initial query from URL parameter
  useEffect(() => {
    if (initialQuery) {
      setFilters(prev => ({ ...prev, query: initialQuery }))
    }
  }, [initialQuery])

  // Mock product data with enhanced categorization
  const mockProducts: VendorProduct[] = useMemo(() => [
    {
      id: 'thai-7oh-powder-1',
      name: 'Premium Thai 7OH Powder',
      description: 'High-quality Thai-sourced 7OH mitragynine powder with fluffy texture and euphoric effects.',
      extractType: 'Thai 7OH Powder',
      type: 'powder',
      sizes: [
        { weight: '1g', price: 29.99, inStock: true },
        { weight: '5g', price: 129.99, inStock: true }
      ],
      images: ['/api/placeholder/400/300'],
      inStock: true,
      featured: true,
      categories: ['7OH Products', 'Thai Products', 'Euphoric'],
      tags: ['thai', 'fluffy', 'euphoric', 'energetic', 'premium'],
      dateAdded: new Date('2024-01-15'),
      salesCount: 234,
      rating: 4.8,
      reviewCount: 45,
      potency: '8.2% 7OH',
      effects: ['Euphoric', 'Energetic']
    },
    {
      id: 'domestic-7oh-powder-1',
      name: 'Crystalline Domestic 7OH',
      description: 'Domestic-sourced 7OH mitragynine with crystalline structure, perfect for sedating effects.',
      extractType: 'Domestic 7OH Powder',
      type: 'powder',
      sizes: [
        { weight: '1g', price: 24.99, inStock: true },
        { weight: '5g', price: 109.99, inStock: true }
      ],
      images: ['/api/placeholder/400/300'],
      inStock: true,
      featured: false,
      categories: ['7OH Products', 'Domestic Products', 'Sedating'],
      tags: ['domestic', 'crystalline', 'sedating', 'pain-relief'],
      dateAdded: new Date('2024-01-20'),
      salesCount: 189,
      rating: 4.6,
      reviewCount: 32,
      potency: '7.8% 7OH',
      effects: ['Sedating', 'Pain Relieving']
    },
    {
      id: 'thai-7oh-tablets-1',
      name: 'Thai 7OH Tablets',
      description: 'Precisely dosed Thai 7OH tablets for consistent euphoric effects.',
      extractType: 'Thai 7OH Tablets',
      type: 'tablet',
      sizes: [
        { weight: '10 tablets', price: 34.99, inStock: true },
        { weight: '25 tablets', price: 79.99, inStock: true }
      ],
      images: ['/api/placeholder/400/300'],
      inStock: true,
      featured: true,
      categories: ['7OH Products', 'Thai Products', 'Tablets'],
      tags: ['thai', 'tablets', 'euphoric', 'consistent-dosing'],
      dateAdded: new Date('2024-01-25'),
      salesCount: 156,
      rating: 4.9,
      reviewCount: 28,
      potency: '10mg per tablet',
      effects: ['Euphoric', 'Energetic']
    },
    {
      id: 'mit-salt-powder-1',
      name: 'MIT Salt Extract Powder',
      description: 'High-purity mitragynine salt powder for enhanced bioavailability and pain relief.',
      extractType: 'MIT Salt Powder',
      type: 'powder',
      sizes: [
        { weight: '2g', price: 19.99, inStock: true },
        { weight: '10g', price: 89.99, inStock: true }
      ],
      images: ['/api/placeholder/400/300'],
      inStock: true,
      featured: false,
      categories: ['MIT Products', 'Salt Form', 'Pain Relief'],
      tags: ['mit', 'salt', 'pain-relief', 'bioavailable'],
      dateAdded: new Date('2024-02-01'),
      salesCount: 98,
      rating: 4.4,
      reviewCount: 18,
      potency: '65% MIT',
      effects: ['Pain Relieving', 'Sedating']
    },
    {
      id: 'mit-freebase-powder-1',
      name: 'MIT Freebase Extract',
      description: 'Pure mitragynine freebase powder for energetic and euphoric experiences.',
      extractType: 'MIT Freebase Powder',
      type: 'powder',
      sizes: [
        { weight: '2g', price: 22.99, inStock: true },
        { weight: '10g', price: 99.99, inStock: false }
      ],
      images: ['/api/placeholder/400/300'],
      inStock: true,
      featured: false,
      categories: ['MIT Products', 'Freebase Form', 'Energetic'],
      tags: ['mit', 'freebase', 'energetic', 'euphoric'],
      dateAdded: new Date('2024-02-05'),
      salesCount: 67,
      rating: 4.3,
      reviewCount: 12,
      potency: '58% MIT',
      effects: ['Energetic', 'Euphoric']
    },
    {
      id: 'red-maeng-da-1',
      name: 'Premium Red Maeng Da',
      description: 'Traditional red vein kratom powder known for sedating and pain-relieving properties.',
      extractType: 'Plain Leaf',
      type: 'powder',
      sizes: [
        { weight: '1oz', price: 12.99, inStock: true },
        { weight: '4oz', price: 45.99, inStock: true },
        { weight: '8oz', price: 79.99, inStock: true }
      ],
      images: ['/api/placeholder/400/300'],
      inStock: true,
      featured: false,
      categories: ['Plain Leaf', 'Red Strains', 'Traditional'],
      tags: ['red-vein', 'maeng-da', 'sedating', 'traditional'],
      dateAdded: new Date('2024-01-10'),
      salesCount: 445,
      rating: 4.7,
      reviewCount: 89,
      potency: '1.2% MIT naturally',
      effects: ['Sedating', 'Pain Relieving']
    }
  ], [])

  // Calculate search results with premium placement algorithm
  const searchResults = useMemo(() => {
    setIsSearching(true)
    
    let results: SearchResult[] = []
    
    // Get all vendor-product combinations
    mockVendors.forEach(vendor => {
      mockProducts.forEach(product => {
        // Basic filtering
        if (!passesFilters(product, filters)) return
        
        // Calculate relevance score
        const score = calculateRelevanceScore(product, filters, vendor)
        
        // Determine premium status
        const isPremiumVendor = ['professional', 'enterprise'].includes(vendor.subscriptionPlan || 'basic')
        
        results.push({
          product,
          vendor,
          score,
          isFeatured: product.featured,
          isPremiumVendor
        })
      })
    })
    
    // Apply sorting with premium placement
    results = applySortingWithPremiumPlacement(results, filters)
    
    setTimeout(() => setIsSearching(false), 300)
    return results
  }, [filters, mockProducts])

  // Filter products based on current filters
  function passesFilters(product: VendorProduct, filters: SearchFilters): boolean {
    // Text search
    if (filters.query) {
      const searchText = filters.query.toLowerCase()
      const searchableText = [
        product.name,
        product.description,
        product.extractType,
        ...product.tags,
        ...product.categories,
        ...(product.effects || [])
      ].join(' ').toLowerCase()
      
      if (!searchableText.includes(searchText)) return false
    }
    
    // Product type filter
    if (filters.productType !== 'all') {
      const extractLower = product.extractType.toLowerCase()
      switch (filters.productType) {
        case '7oh':
          if (!extractLower.includes('7oh')) return false
          break
        case 'mit':
          if (!extractLower.includes('mit') || extractLower.includes('7oh')) return false
          break
        case 'plain-leaf':
          if (extractLower.includes('7oh') || extractLower.includes('mit')) return false
          break
      }
    }
    
    // Source type filter (Thai vs Domestic)
    if (filters.sourceType !== 'all') {
      const extractLower = product.extractType.toLowerCase()
      if (filters.sourceType === 'thai' && !extractLower.includes('thai')) return false
      if (filters.sourceType === 'domestic' && !extractLower.includes('domestic')) return false
    }
    
    // Chemical form filter (Salt vs Freebase for MIT)
    if (filters.chemicalForm !== 'all') {
      const extractLower = product.extractType.toLowerCase()
      if (extractLower.includes('mit')) {
        if (filters.chemicalForm === 'salt' && !extractLower.includes('salt')) return false
        if (filters.chemicalForm === 'freebase' && !extractLower.includes('freebase')) return false
      }
    }
    
    // Format filter
    if (filters.format !== 'all' && product.type !== filters.format) return false
    
    // Effects filter
    if (filters.effects.length > 0) {
      const productEffects = product.effects || []
      if (!filters.effects.some(effect => productEffects.includes(effect))) return false
    }
    
    // Price range filter
    const minPrice = Math.min(...product.sizes.map(s => s.price))
    if (minPrice < filters.priceRange[0] || minPrice > filters.priceRange[1]) return false
    
    // Rating filter
    if (product.rating < filters.minRating) return false
    
    return true
  }
  
  // Calculate relevance score for search ranking
  function calculateRelevanceScore(product: VendorProduct, filters: SearchFilters, vendor: Vendor): number {
    let score = 0
    
    // Base relevance score
    if (filters.query) {
      const query = filters.query.toLowerCase()
      if (product.name.toLowerCase().includes(query)) score += 100
      if (product.extractType.toLowerCase().includes(query)) score += 80
      if (product.description.toLowerCase().includes(query)) score += 40
      if (product.tags.some(tag => tag.toLowerCase().includes(query))) score += 60
    } else {
      score = 50 // Base score when no query
    }
    
    // Product quality indicators
    score += product.rating * 10
    score += Math.min(product.reviewCount, 50)
    score += Math.min(product.salesCount / 10, 50)
    
    // Vendor quality boost
    if (vendor.verified) score += 20
    if (vendor.rating > 4.5) score += 15
    
    // Recent products boost
    const daysSinceAdded = (Date.now() - product.dateAdded.getTime()) / (1000 * 60 * 60 * 24)
    if (daysSinceAdded < 30) score += 10
    
    return score
  }
  
  // Apply sorting with premium placement logic
  function applySortingWithPremiumPlacement(results: SearchResult[], filters: SearchFilters): SearchResult[] {
    // First, separate premium and regular results
    const premiumResults = results.filter(r => r.isPremiumVendor)
    const regularResults = results.filter(r => !r.isPremiumVendor)
    
    // Sort each group
    const sortFunction = getSortFunction(filters.sortBy)
    premiumResults.sort(sortFunction)
    regularResults.sort(sortFunction)
    
    // Handle featured products
    if (filters.showFeaturedFirst) {
      const featuredPremium = premiumResults.filter(r => r.isFeatured)
      const nonFeaturedPremium = premiumResults.filter(r => !r.isFeatured)
      const featuredRegular = regularResults.filter(r => r.isFeatured)
      const nonFeaturedRegular = regularResults.filter(r => !r.isFeatured)
      
      return [
        ...featuredPremium,
        ...featuredRegular, 
        ...nonFeaturedPremium,
        ...nonFeaturedRegular
      ]
    }
    
    // Premium placement: show premium vendors first
    return [...premiumResults, ...regularResults]
  }
  
  // Get sorting function based on sort type
  function getSortFunction(sortBy: string) {
    switch (sortBy) {
      case 'price-low':
        return (a: SearchResult, b: SearchResult) => {
          const aPrice = Math.min(...a.product.sizes.map(s => s.price))
          const bPrice = Math.min(...b.product.sizes.map(s => s.price))
          return aPrice - bPrice
        }
      case 'price-high':
        return (a: SearchResult, b: SearchResult) => {
          const aPrice = Math.min(...a.product.sizes.map(s => s.price))
          const bPrice = Math.min(...b.product.sizes.map(s => s.price))
          return bPrice - aPrice
        }
      case 'rating':
        return (a: SearchResult, b: SearchResult) => b.product.rating - a.product.rating
      case 'newest':
        return (a: SearchResult, b: SearchResult) => b.product.dateAdded.getTime() - a.product.dateAdded.getTime()
      case 'popular':
        return (a: SearchResult, b: SearchResult) => b.product.salesCount - a.product.salesCount
      case 'relevance':
      default:
        return (a: SearchResult, b: SearchResult) => b.score - a.score
    }
  }

  // Update filter
  const updateFilter = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  // Toggle effect filter
  const toggleEffect = (effect: string) => {
    setFilters(prev => ({
      ...prev,
      effects: prev.effects.includes(effect)
        ? prev.effects.filter(e => e !== effect)
        : [...prev.effects, effect]
    }))
  }

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      query: '',
      productType: 'all',
      sourceType: 'all',
      chemicalForm: 'all',
      format: 'all',
      effects: [],
      priceRange: [0, 500],
      minRating: 0,
      sortBy: 'relevance',
      showFeaturedFirst: true
    })
  }

  // Send results to parent component
  useEffect(() => {
    onResults?.(searchResults)
  }, [searchResults, onResults])

  return (
    <div className={`enhanced-search ${compactMode ? 'compact' : ''}`}>
      {/* Main Search Bar */}
      <div className="search-bar-container">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="Search for 7OH, MIT, strains, effects..."
            value={filters.query}
            onChange={(e) => updateFilter('query', e.target.value)}
            className="search-input"
          />
          <button className="search-button">
            🔍
          </button>
        </div>
        
        {showFilters && (
          <button 
            className="advanced-filters-toggle"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            Filters {showAdvancedFilters ? '▲' : '▼'}
            {(filters.productType !== 'all' || filters.effects.length > 0) && (
              <span className="active-filters-indicator">●</span>
            )}
          </button>
        )}
      </div>

      {/* Quick Filter Pills */}
      <div className="quick-filters">
        <button 
          className={`filter-pill ${filters.productType === '7oh' ? 'active' : ''}`}
          onClick={() => updateFilter('productType', filters.productType === '7oh' ? 'all' : '7oh')}
        >
          7OH Products
        </button>
        <button 
          className={`filter-pill ${filters.productType === 'mit' ? 'active' : ''}`}
          onClick={() => updateFilter('productType', filters.productType === 'mit' ? 'all' : 'mit')}
        >
          MIT Products
        </button>
        <button 
          className={`filter-pill ${filters.productType === 'plain-leaf' ? 'active' : ''}`}
          onClick={() => updateFilter('productType', filters.productType === 'plain-leaf' ? 'all' : 'plain-leaf')}
        >
          Plain Leaf
        </button>
        <button 
          className={`filter-pill ${filters.effects.includes('Euphoric') ? 'active' : ''}`}
          onClick={() => toggleEffect('Euphoric')}
        >
          Euphoric
        </button>
        <button 
          className={`filter-pill ${filters.effects.includes('Sedating') ? 'active' : ''}`}
          onClick={() => toggleEffect('Sedating')}
        >
          Sedating
        </button>
        <button 
          className={`filter-pill ${filters.effects.includes('Energetic') ? 'active' : ''}`}
          onClick={() => toggleEffect('Energetic')}
        >
          Energizing
        </button>
        <button 
          className={`filter-pill ${filters.effects.includes('Pain Relieving') ? 'active' : ''}`}
          onClick={() => toggleEffect('Pain Relieving')}
        >
          Pain Relief
        </button>
        <button 
          className={`filter-pill ${filters.format === 'capsule' ? 'active' : ''}`}
          onClick={() => updateFilter('format', filters.format === 'capsule' ? 'all' : 'capsule')}
        >
          Capsules
        </button>
        <button 
          className={`filter-pill ${filters.format === 'powder' ? 'active' : ''}`}
          onClick={() => updateFilter('format', filters.format === 'powder' ? 'all' : 'powder')}
        >
          Powder
        </button>
        <button 
          className={`filter-pill ${filters.format === 'tablet' ? 'active' : ''}`}
          onClick={() => updateFilter('format', filters.format === 'tablet' ? 'all' : 'tablet')}
        >
          Tablets
        </button>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && showFilters && (
        <div className="advanced-filters">
          <div className="filters-grid">
            
            {/* Product Type */}
            <div className="filter-group">
              <label>Product Type</label>
              <select 
                value={filters.productType} 
                onChange={(e) => updateFilter('productType', e.target.value)}
              >
                {Object.entries(productTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            {/* Source Type (Thai/Domestic) */}
            {(filters.productType === '7oh' || filters.productType === 'all') && (
              <div className="filter-group">
                <label>Source Type</label>
                <select 
                  value={filters.sourceType} 
                  onChange={(e) => updateFilter('sourceType', e.target.value)}
                >
                  <option value="all">All Sources</option>
                  <option value="thai">Thai (Fluffy)</option>
                  <option value="domestic">Domestic (Crystalline)</option>
                </select>
              </div>
            )}

            {/* Chemical Form (Salt/Freebase) */}
            {filters.productType === 'mit' && (
              <div className="filter-group">
                <label>Chemical Form</label>
                <select 
                  value={filters.chemicalForm} 
                  onChange={(e) => updateFilter('chemicalForm', e.target.value)}
                >
                  <option value="all">All Forms</option>
                  <option value="salt">Salt Form</option>
                  <option value="freebase">Freebase Form</option>
                </select>
              </div>
            )}

            {/* Format */}
            <div className="filter-group">
              <label>Format</label>
              <select 
                value={filters.format} 
                onChange={(e) => updateFilter('format', e.target.value)}
              >
                <option value="all">All Formats</option>
                <option value="powder">Powder</option>
                <option value="tablet">Tablet</option>
                <option value="capsule">Capsule</option>
              </select>
            </div>

            {/* Rating Filter */}
            <div className="filter-group">
              <label>Minimum Rating</label>
              <select 
                value={filters.minRating} 
                onChange={(e) => updateFilter('minRating', Number(e.target.value))}
              >
                <option value={0}>Any Rating</option>
                <option value={3}>3+ Stars</option>
                <option value={4}>4+ Stars</option>
                <option value={4.5}>4.5+ Stars</option>
              </select>
            </div>

            {/* Sort By */}
            <div className="filter-group">
              <label>Sort By</label>
              <select 
                value={filters.sortBy} 
                onChange={(e) => updateFilter('sortBy', e.target.value)}
              >
                <option value="relevance">Relevance</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
          </div>

          {/* Effects Filter */}
          <div className="filter-group full-width">
            <label>Desired Effects</label>
            <div className="effects-grid">
              {effectOptions.map(effect => (
                <button
                  key={effect}
                  className={`effect-btn ${filters.effects.includes(effect) ? 'active' : ''}`}
                  onClick={() => toggleEffect(effect)}
                >
                  {effect}
                </button>
              ))}
            </div>
          </div>

          {/* Price Range */}
          <div className="filter-group full-width">
            <label>Price Range: ${filters.priceRange[0]} - ${filters.priceRange[1]}</label>
            <div className="price-range-inputs">
              <input
                type="number"
                placeholder="Min"
                value={filters.priceRange[0]}
                onChange={(e) => updateFilter('priceRange', [Number(e.target.value), filters.priceRange[1]])}
              />
              <span>to</span>
              <input
                type="number"
                placeholder="Max"
                value={filters.priceRange[1]}
                onChange={(e) => updateFilter('priceRange', [filters.priceRange[0], Number(e.target.value)])}
              />
            </div>
          </div>

          {/* Premium Options */}
          <div className="filter-group full-width">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={filters.showFeaturedFirst}
                onChange={(e) => updateFilter('showFeaturedFirst', e.target.checked)}
              />
              Show featured products first
            </label>
          </div>

          {/* Clear Filters */}
          <div className="filter-actions">
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Results Summary */}
      <div className="search-summary">
        <span className="results-count">
          {isSearching ? 'Searching...' : `${searchResults.length} products found`}
        </span>
        {searchResults.filter(r => r.isFeatured).length > 0 && (
          <span className="featured-count">
            ({searchResults.filter(r => r.isFeatured).length} featured)
          </span>
        )}
      </div>

      <style>{`
        .enhanced-search {
          background: #2d3748;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          margin-bottom: 20px;
        }

        .enhanced-search.compact {
          padding: 15px;
          margin-bottom: 15px;
        }

        .search-bar-container {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          align-items: center;
        }

        .search-input-wrapper {
          flex: 1;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 15px 50px 15px 15px;
          border: 2px solid #4a5568;
          border-radius: 8px;
          font-size: 16px;
          background: #1a202c;
          color: #e2e8f0;
          transition: border-color 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #2c5530;
        }

        .search-button {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
        }

        .advanced-filters-toggle {
          padding: 15px 20px;
          background: #2d3748;
          border: 1px solid #4a5568;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          color: #e2e8f0;
          position: relative;
          transition: all 0.3s ease;
        }

        .advanced-filters-toggle:hover {
          background: #4a5568;
        }

        .active-filters-indicator {
          position: absolute;
          top: 5px;
          right: 5px;
          color: #2c5530;
          font-size: 12px;
        }

        .quick-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 20px;
        }

        .filter-pill {
          padding: 8px 16px;
          background: #f5f5f5;
          border: 1px solid #e0e0e0;
          border-radius: 20px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .filter-pill:hover {
          background: #e8e8e8;
        }

        .filter-pill.active {
          background: #2c5530;
          color: white;
          border-color: #2c5530;
        }

        .advanced-filters {
          background: #f9f9f9;
          border-radius: 8px;
          padding: 20px;
          margin-top: 15px;
          border: 1px solid #e0e0e0;
        }

        .filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 20px;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-group.full-width {
          grid-column: 1 / -1;
        }

        .filter-group label {
          font-weight: 600;
          color: #e2e8f0;
          font-size: 14px;
        }

        .filter-group select {
          padding: 10px;
          border: 1px solid #4a5568;
          border-radius: 4px;
          background: #2d3748;
        }

        .effects-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 10px;
        }

        .effect-btn {
          padding: 10px;
          background: #2d3748;
          border: 1px solid #4a5568;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .effect-btn:hover {
          background: #f5f5f5;
        }

        .effect-btn.active {
          background: #2c5530;
          color: white;
          border-color: #2c5530;
        }

        .price-range-inputs {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .price-range-inputs input {
          flex: 1;
          padding: 8px;
          border: 1px solid #4a5568;
          border-radius: 4px;
        }

        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .checkbox-label input {
          margin: 0;
        }

        .filter-actions {
          text-align: center;
          margin-top: 20px;
        }

        .clear-filters-btn {
          padding: 10px 20px;
          background: #f44336;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
        }

        .clear-filters-btn:hover {
          background: #d32f2f;
        }

        .search-summary {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 0;
          border-top: 1px solid #e0e0e0;
          margin-top: 20px;
          font-size: 14px;
          color: #a0aec0;
        }

        .results-count {
          font-weight: 600;
          color: #e2e8f0;
        }

        .featured-count {
          color: #2c5530;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .search-bar-container {
            flex-direction: column;
            align-items: stretch;
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }

          .effects-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .quick-filters {
            justify-content: center;
          }
        }
      `}</style>
    </div>
  )
}